import { useCallback, useEffect, useRef } from 'react';

import { useFocusEffect, useIsFocused } from '@react-navigation/native';

import { useStableCallback } from '@/hooks/use-stable-callback';
import { useStableIds } from '@/hooks/use-stable-ids';
import { useWebsocket } from '@/utils/websocket/provider';
import { AuthEvents, AuthMessageMap, PublicChannel, PublicChannelMap } from '@/utils/websocket/types';
import { ChannelOptions } from 'ably';

/**
 * Subscription hook for listening from multiple eventIds from public channels
 */
export const useSubscribeMultipleEventPublic = <T extends keyof PublicChannelMap>(
    channelTemplate: PublicChannel<T>['channel'],
    eventName: PublicChannel<T>['eventName'],
    eventIds: string[],
    handler: (data: any) => void,
    enabled: boolean = true
) => {
    const { websocketService, connected } = useWebsocket();
    const isFocused = useIsFocused();
    const active = !!websocketService && connected && enabled && isFocused;

    const stableHandler = useStableCallback(handler);
    const stableEventIds = useStableIds(eventIds);
    const listeners = useRef(new Map<string, (e: { data: any }) => void>());

    // Cleanup runs before the sync effect below, so a session change resubscribes everything
    useEffect(() => {
        const current = listeners.current;
        return () => {
            current.forEach((listener, eventId) => {
                websocketService?.unsubscribePublic(channelTemplate, { eventId }, eventName, listener);
            });
            current.clear();
        };
    }, [active, channelTemplate, eventName, websocketService]);

    useEffect(() => {
        if (!active) {
            return;
        }
        const current = listeners.current;
        const nextIds = new Set(stableEventIds);
        current.forEach((listener, eventId) => {
            if (!nextIds.has(eventId)) {
                websocketService.unsubscribePublic(channelTemplate, { eventId }, eventName, listener);
                current.delete(eventId);
            }
        });
        nextIds.forEach(eventId => {
            if (!current.has(eventId)) {
                const listener = websocketService.subscribePublic(
                    channelTemplate,
                    { eventId },
                    eventName,
                    stableHandler
                );
                current.set(eventId, listener);
            }
        });
    }, [active, channelTemplate, eventName, stableEventIds, stableHandler, websocketService]);
};

/**
 * Subscription hook for listening to events from public channels
 */
export const useSubscribePublic = <T extends keyof PublicChannelMap>(
    channelTemplate: PublicChannel<T>['channel'],
    params: PublicChannel<T>['params'],
    eventName: PublicChannel<T>['eventName'],
    handler: (data: PublicChannel<T>['message']) => void,
    enabled: boolean = true,
    options?: ChannelOptions
) => {
    const { websocketService, connected } = useWebsocket();

    useFocusEffect(
        useCallback(() => {
            if (connected && enabled) {
                const listener = websocketService?.subscribePublic(
                    channelTemplate,
                    params,
                    eventName,
                    handler,
                    options
                );
                return () => {
                    if (listener) {
                        websocketService?.unsubscribePublic(channelTemplate, params, eventName, listener);
                    }
                };
            }
        }, [connected, enabled, websocketService, channelTemplate, params, eventName, handler, options])
    );
};

/**
 * Subscription hook for listening to events from authenticated channels
 */
export const useSubscribeAuth = <T extends keyof AuthMessageMap>(
    eventName: AuthEvents<T>['eventName'],
    handler: (data: AuthEvents<T>['message']) => void,
    enabled: boolean = true
) => {
    const { websocketService, connected, authenticated } = useWebsocket();
    useEffect(() => {
        if (connected && enabled && authenticated) {
            websocketService?.subscribeAuth(eventName, handler);
            return () => {
                websocketService?.unsubscribeAuth(eventName);
            };
        }
    }, [authenticated, eventName, handler, connected, enabled, websocketService]);
};
